// =====================================================================
// Embauche.jsx — M-011 (26-08-2026) — page PUBLIQUE /embauche/<token>
// =====================================================================
// Le candidat (CDD/CDI) complète son dossier depuis son téléphone via le
// QR/lien reçu. Aucune session : le token à usage unique EST l'accès.
// Connu → champs préremplis (IBAN masqué, Carte ID « déjà fournie ») ;
// il confirme ou corrige. Tout est obligatoire (demande Aurélie/Florent).
// =====================================================================
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { onboarding, errMsg } from '../services/api.js';
import { lettresDuNom } from '../lib/noms.js';

const MARITAL = [
  { v: 'CELIBATAIRE', label: 'Célibataire' },
  { v: 'MARIE', label: 'Marié(e)' },
  { v: 'COHABITANT_LEGAL', label: 'Cohabitant légal' },
];

const wrap = { maxWidth: 560, margin: '0 auto', padding: '20px 16px 60px', fontFamily: 'system-ui, sans-serif' };
const lbl = { display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', margin: '14px 0 4px' };
const inp = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 15, border: '1px solid #cbd5e1', borderRadius: 8 };
const req = <span style={{ color: '#dc2626' }}> *</span>;

// Une photo de carte d'identité prise au téléphone pèse 2 à 6 Mo. Deux comme ça
// dépassent la limite Microsoft de 4 Mo par mail (le dossier partirait aux contrats
// SANS les photos) et, sur une connexion mobile lente, l'envoi dure très longtemps.
// On réduit donc l'image ICI, avant l'envoi : côté long 2000 px (la carte reste
// parfaitement lisible), JPEG 85 % — ~0,5 Mo mesuré. Les PDF passent tels quels.
const PHOTO_COTE_MAX = 2000;
const PHOTO_SEUIL_OCTETS = 900 * 1024;

const DELAI_MS = 10000;

// Décodage de la photo, borné dans le temps : un décodage qui ne répond jamais
// (téléphone à court de mémoire) est abandonné ET nettoyé (image + URL libérées),
// sinon un nouvel essai empilerait un 2e décodage sur le 1er.
function chargerImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    const fin = () => { clearTimeout(minuterie); img.onload = null; img.onerror = null; URL.revokeObjectURL(url); };
    const minuterie = setTimeout(() => { fin(); img.src = ''; reject(new Error('délai dépassé')); }, DELAI_MS);
    img.onload = () => { fin(); resolve(img); };
    img.onerror = () => { fin(); reject(new Error('image illisible')); };
    img.src = url;
  });
}

function versBlob(canvas) {
  return new Promise((resolve, reject) => {
    const minuterie = setTimeout(() => reject(new Error('délai dépassé')), DELAI_MS);
    canvas.toBlob((blob) => { clearTimeout(minuterie); resolve(blob); }, 'image/jpeg', 0.85);
  });
}

// Une réduction par fichier choisi : un nouvel essai après une erreur ne refait pas
// le travail (lent sur un téléphone d'entrée de gamme). Seul un ÉCHEC (mémoire,
// délai) n'est pas gardé, pour être retenté ; « pas de gain » est un résultat, gardé.
const photosReduites = new WeakMap();

const aReduire = (file) => !!file && /^image\/(jpeg|png)$/.test(file.type) && file.size > PHOTO_SEUIL_OCTETS;

function reduirePhoto(file) {
  if (!aReduire(file)) return Promise.resolve(file);
  if (!photosReduites.has(file)) {
    photosReduites.set(file, reduireMaintenant(file).then(({ fichier, echec }) => {
      if (echec) photosReduites.delete(file);
      return fichier;
    }));
  }
  return photosReduites.get(file);
}

async function reduireMaintenant(file) {
  let canvas = null;
  try {
    // <img> + drawImage respecte l'orientation EXIF (photo prise en portrait) ;
    // createImageBitmap ne le fait pas de la même façon selon les navigateurs.
    const img = await chargerImage(file);
    const echelle = Math.min(1, PHOTO_COTE_MAX / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.round(img.naturalWidth * echelle);
    const h = Math.round(img.naturalHeight * echelle);
    canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff'; // PNG transparent → fond blanc une fois en JPEG
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await versBlob(canvas);
    if (!blob) return { fichier: file, echec: true };
    if (blob.size >= file.size) return { fichier: file, echec: false }; // déjà compacte
    return { fichier: new File([blob], `${file.name.replace(/\.[^.]+$/, '')}.jpg`, { type: 'image/jpeg', lastModified: Date.now() }), echec: false };
  } catch {
    return { fichier: file, echec: true }; // ne jamais bloquer le candidat : l'original part
  } finally {
    // iOS Safari plafonne la mémoire totale des canvas : on libère tout de suite.
    if (canvas) { canvas.width = 0; canvas.height = 0; }
  }
}

export default function Embauche() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [state, setState] = useState('loading'); // loading | form | sending | done | invalid
  const [preparation, setPreparation] = useState(false); // photos en cours de réduction (avant l'envoi)
  const [error, setError] = useState('');
  const [f, setF] = useState({
    // Ordre calé sur l'écran SD Worx « Ajouter nouveau contrat — Étape 2/2 › Identification »
    // (demande Aurélie 30-09-2026) pour qu'elle recopie de haut en bas, sans chercher.
    lastName: '', firstName: '', idCardNumber: '', niss: '',
    nationality: '', gender: '', language: '',
    // ⚠️ '' et PAS 'false' : ces deux cases partent TOUJOURS dans le formulaire.
    // Avec 'false' par défaut, un collaborateur déjà reconnu handicapé qui repassait
    // par le formulaire repartait à « non » sans le voir (mesuré : true -> false).
    // '' = « pas répondu » → le champ n'est pas envoyé → la valeur au dossier reste.
    disabledWorker: '', survivorPension: '',
    birthPlace: '', address: '', iban: '', dependentsCount: '0',
    maritalStatus: '', spouseName: '', spouseWorks: '', spouseBirthDate: '', spouseIncome: '',
    email: '', phone: '',
  });
  const [recto, setRecto] = useState(null);
  const [verso, setVerso] = useState(null);

  useEffect(() => {
    onboarding.publicGet(token)
      .then((inv) => {
        setInvite(inv);
        const p = inv.prefill;
        if (p) {
          setF((old) => ({
            ...old,
            lastName: p.lastName || '', firstName: p.firstName || '',
            idCardNumber: p.idCardNumber || '', niss: p.niss || '',
            nationality: p.nationality || '', gender: p.gender || '', language: p.language || '',
            disabledWorker: p.disabledWorker == null ? '' : String(p.disabledWorker),
            survivorPension: p.survivorPension == null ? '' : String(p.survivorPension),
            spouseBirthDate: p.spouseBirthDate || '', spouseIncome: p.spouseIncome || '',
            email: p.email || '', birthPlace: p.birthPlace || '',
            address: p.address || '',
            dependentsCount: p.dependentsCount != null ? String(p.dependentsCount) : '0',
            maritalStatus: p.maritalStatus || '',
            spouseName: p.spouseName || '',
            spouseWorks: p.spouseWorks == null ? '' : String(p.spouseWorks),
          }));
        } else if (inv.kind !== 'PROLONGATION') {
          // Candidat NOUVEAU : aucune valeur au dossier à protéger, donc une case non
          // cochée vaut « non ». Sans ça la colonne resterait null et le mail contrats
          // afficherait « — » pour deux réponses que SD Worx exige — Aurélie devrait
          // les redemander. Pour un collaborateur CONNU on garde '' (= inchangé).
          setF((old) => ({ ...old, disabledWorker: 'false', survivorPension: 'false' }));
        }
        // ⚠️ On ne préremplit PAS le nom à partir de `candidateName` : découper au 1er
        // espace suppose « NOM Prénom », alors que l'étiquette peut être dans les deux
        // ordres. Un mauvais préremplissage accepté tel quel envoie un faux nom de
        // famille à SD Worx — pire qu'une case vide, que la personne remplit elle-même.
        setState('form');
      })
      .catch((e) => {
        // Prolongation déjà confirmée : le lien se reclique souvent (mail rouvert).
        // Un écran rouge « lien invalide » ferait croire que sa confirmation a échoué.
        // ⚠️ error-handler.js renvoie le code dans le champ « error », pas « code ».
        const apiCode = e?.response?.data?.error || e?.response?.data?.code;
        if (apiCode === 'ALREADY_CONFIRMED') setState('confirmed');
        else setState('invalid');
      });
  }, [token]);

  const set = (k) => (e) => setF((old) => ({ ...old, [k]: e.target.value }));
  const needSpouse = ['MARIE', 'COHABITANT_LEGAL'].includes(f.maritalStatus);
  const hasCi = !!invite?.prefill?.hasCarteId;
  const hasIban = !!invite?.prefill?.hasIban;

  const missing = useMemo(() => {
    const m = [];
    // Au moins 2 lettres — la même règle que le serveur (lib/noms.js).
    if (lettresDuNom(f.lastName) < 2) m.push('Nom');
    if (lettresDuNom(f.firstName) < 2) m.push('Prénom');
    if (f.niss.replace(/\D/g, '').length !== 11) m.push('N° NISS (11 chiffres)');
    if (!/\S+@\S+\.\S+/.test(f.email)) m.push('Email');
    if (f.birthPlace.trim().length < 2) m.push('Lieu de naissance');
    if (f.address.trim().length < 5) m.push('Adresse');
    if (!f.iban.trim() && !hasIban) m.push('IBAN');
    if (f.dependentsCount === '') m.push('Personnes à charge');
    if (f.maritalStatus === '') m.push('État civil');
    if (needSpouse && !f.spouseName.trim()) m.push('Nom du conjoint');
    if (needSpouse && f.spouseWorks === '') m.push('Conjoint travaille (oui/non)');
    if (needSpouse && !f.spouseBirthDate) m.push('Date de naissance du conjoint');
    if (needSpouse && f.spouseWorks === 'true' && !f.spouseIncome.trim()) m.push('Revenu du conjoint');
    if (!hasCi && !recto) m.push('Carte ID — recto');
    if (!hasCi && !verso) m.push('Carte ID — verso');
    return m;
  }, [f, recto, verso, needSpouse, hasCi, hasIban]);

  async function submit(e) {
    e.preventDefault();
    if (missing.length) { setError(`Champs manquants : ${missing.join(', ')}`); return; }
    setError('');
    setState('sending');
    try {
      const fd = new FormData();
      for (const [k, v] of Object.entries(f)) {
        // câmpurile de conjoint nu se trimit deloc dacă starea civilă nu le cere
        if (['spouseName', 'spouseWorks', 'spouseBirthDate', 'spouseIncome'].includes(k) && !needSpouse) continue;
        // champs optionnels : '' ne doit PAS écraser une valeur existante côté serveur
        if (v === '' && ['spouseWorks', 'spouseName', 'spouseBirthDate', 'spouseIncome',
          'phone', 'iban', 'idCardNumber', 'nationality', 'gender', 'language',
          // 30-09-2026 : les deux cases à cocher aussi. Non cochées ET jamais touchées = ''
          // = « pas répondu » → on n'envoie rien, la valeur au dossier reste. Une case
          // cochée puis décochée vaut 'false' : c'est une réponse explicite, elle part.
          'disabledWorker', 'survivorPension'].includes(k)) continue;
        fd.append(k, v ?? '');
      }
      // L'une après l'autre : deux photos 12 Mpx décodées en même temps peuvent épuiser
      // la mémoire d'un téléphone d'entrée de gamme (et alors l'original partirait).
      setPreparation([recto, verso].some(aReduire));
      const rectoEnvoi = await reduirePhoto(recto);
      const versoEnvoi = await reduirePhoto(verso);
      setPreparation(false);
      if (rectoEnvoi) fd.append('recto', rectoEnvoi);
      if (versoEnvoi) fd.append('verso', versoEnvoi);
      await onboarding.publicSubmit(token, fd);
      setState('done');
    } catch (err) {
      setPreparation(false);
      setError(errMsg(err));
      setState('form');
    }
  }

  if (state === 'loading') return <div style={wrap}>Chargement…</div>;
  if (state === 'invalid') {
    return (
      <div style={wrap}>
        <h2 style={{ color: '#0f172a' }}>Brain E-Log</h2>
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: 16, color: '#991b1b' }}>
          Ce lien d'embauche est <strong>invalide, déjà utilisé ou expiré</strong>.<br />
          Contactez la personne qui vous l'a envoyé pour recevoir un nouveau lien.
        </div>
      </div>
    );
  }
  if (state === 'done') {
    return (
      <div style={wrap}>
        <h2 style={{ color: '#0f172a' }}>Brain E-Log</h2>
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 16, color: '#166534' }}>
          ✅ <strong>Dossier envoyé, merci {f.firstName} !</strong><br />
          L'équipe Brain va vérifier vos informations. Vous recevrez vos identifiants
          par email une fois le dossier validé. Vous pouvez fermer cette page.
        </div>
      </div>
    );
  }

  // Prolongation confirmée — écran final (sinon le bouton resterait affiché).
  if (state === 'confirmed') {
    return (
      <div style={wrap}>
        <h2 style={{ color: '#0f172a' }}>Brain E-Log</h2>
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 16, color: '#166534' }}>
          ✅ <strong>Merci, c'est confirmé !</strong><br />
          Vos informations sont validées pour la prolongation de votre contrat.
          Vos identifiants ZeusLog habituels restent valables. Vous pouvez fermer cette page.
        </div>
      </div>
    );
  }

  const period = invite.contractStart
    ? (invite.contractEnd ? ` du ${invite.contractStart} au ${invite.contractEnd}` : ` à partir du ${invite.contractStart}`)
    : '';

  // PROLONGATION (30-09-2026) : le dossier est déjà complet chez nous. Aucun
  // formulaire — une seule confirmation. Si quelque chose a changé, la personne
  // répond au mail (c'est plus simple pour elle que de tout re-saisir).
  if (invite.kind === 'PROLONGATION') {
    return (
      <div style={wrap}>
        <h2 style={{ color: '#0f172a', marginBottom: 4 }}>Brain E-Log — prolongation de contrat</h2>
        <p style={{ color: '#475569', fontSize: 14, marginTop: 0 }}>
          Bonjour{invite.candidateName ? ` ${invite.candidateName}` : ''}, votre contrat{' '}
          <strong>{invite.contractType}</strong>{period} est en préparation.
        </p>
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, fontSize: 14, color: '#334155' }}>
          <p style={{ marginTop: 0 }}>
            Vos informations sont déjà chez nous — <strong>vous n'avez rien à remplir</strong>.
            Merci simplement de confirmer qu'elles sont toujours correctes.
          </p>
          {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 10, color: '#991b1b', marginBottom: 10 }}>{error}</div>}
          <button
            type="button"
            disabled={state === 'sending'}
            onClick={async () => {
              setError('');
              setState('sending');
              try {
                await onboarding.publicConfirm(token);
                setState('confirmed');
              } catch (e) {
                setError(e?.response?.data?.message || 'Échec de la confirmation. Réessayez ou répondez au mail.');
                setState('form');
              }
            }}
            style={{
              background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8,
              padding: '12px 20px', fontSize: 15, fontWeight: 600,
              cursor: state === 'sending' ? 'default' : 'pointer', opacity: state === 'sending' ? 0.6 : 1,
            }}
          >
            {state === 'sending' ? 'Envoi…' : 'Confirmer mes informations'}
          </button>
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 0 }}>
            Quelque chose a changé (adresse, compte bancaire, situation familiale) ?
            Répondez simplement au mail que vous avez reçu.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <h2 style={{ color: '#0f172a', marginBottom: 4 }}>Brain E-Log — dossier d'embauche</h2>
      <p style={{ color: '#475569', fontSize: 14, marginTop: 0 }}>
        Contrat <strong>{invite.contractType}</strong>{period}.
        {invite.known
          ? ' Vos informations connues sont préremplies : vérifiez-les et corrigez si nécessaire.'
          : ' Merci de compléter toutes les informations ci-dessous.'}
      </p>

      <form onSubmit={submit}>
        {/* Ordre SD Worx › Identification (Aurélie 30-09-2026) : Nom · Prénom ·
            n° carte d'identité · NISS · état civil · nationalité · sexe · langue ·
            handicapé · pension de survie · lieu de naissance. */}
        <label style={lbl}>Nom (de famille){req}</label>
        <input style={{ ...inp, textTransform: 'uppercase' }} value={f.lastName} onChange={set('lastName')}
          autoComplete="family-name" placeholder="MACAVEI" />

        <label style={lbl}>Prénom{req}</label>
        <input style={inp} value={f.firstName} onChange={set('firstName')}
          autoComplete="given-name" placeholder="Marina-Florentina" />

        <label style={lbl}>Numéro de carte d'identité</label>
        <input style={inp} value={f.idCardNumber} onChange={set('idCardNumber')} placeholder="592-1234567-89" />

        <label style={lbl}>N° de registre national (NISS){req}</label>
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>
          11 chiffres, au dos de votre carte d'identité. Exemple : 91.02.03-746.57
        </div>
        <input style={inp} value={f.niss} onChange={set('niss')} inputMode="numeric" placeholder="91020374657" />

        <label style={lbl}>Nationalité</label>
        <input style={inp} value={f.nationality} onChange={set('nationality')} placeholder="Belge" />

        <label style={lbl}>Sexe</label>
        <select style={inp} value={f.gender} onChange={set('gender')}>
          <option value="">— choisir —</option>
          <option value="F">Femme</option>
          <option value="M">Homme</option>
        </select>

        <label style={lbl}>Langue</label>
        <select style={inp} value={f.language} onChange={set('language')}>
          <option value="">— choisir —</option>
          <option value="FR">Français</option>
          <option value="NL">Néerlandais</option>
          <option value="EN">Anglais</option>
        </select>

        <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={f.disabledWorker === 'true'}
            onChange={(e) => setF((o) => ({ ...o, disabledWorker: e.target.checked ? 'true' : 'false' }))} />
          Travailleur handicapé
        </label>

        <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={f.survivorPension === 'true'}
            onChange={(e) => setF((o) => ({ ...o, survivorPension: e.target.checked ? 'true' : 'false' }))} />
          Je reçois une pension de survie
        </label>

        <label style={lbl}>Email{req}</label>
        <input style={inp} type="email" value={f.email} onChange={set('email')} autoComplete="email" />

        <label style={lbl}>Téléphone</label>
        <input style={inp} type="tel" value={f.phone} onChange={set('phone')} autoComplete="tel" />

        <label style={lbl}>Lieu de naissance{req}</label>
        <input style={inp} value={f.birthPlace} onChange={set('birthPlace')} placeholder="Ville, pays" />

        <label style={lbl}>Adresse complète{req}</label>
        <input style={inp} value={f.address} onChange={set('address')} autoComplete="street-address" placeholder="Rue n°, code postal, ville" />

        <label style={lbl}>IBAN{hasIban ? '' : req}</label>
        {hasIban && (
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>
            Au dossier : <strong>{invite.prefill.ibanMasked}</strong> — laissez vide si inchangé.
          </div>
        )}
        <input style={inp} value={f.iban} onChange={set('iban')} placeholder={hasIban ? 'Nouveau IBAN (optionnel)' : 'BE68 5390 0754 7034'} />

        <label style={lbl}>Personnes à charge{req}</label>
        <input style={inp} type="number" min="0" max="20" value={f.dependentsCount} onChange={set('dependentsCount')} />

        <label style={lbl}>État civil{req}</label>
        <select style={inp} value={f.maritalStatus} onChange={set('maritalStatus')}>
          <option value="">— choisir —</option>
          {MARITAL.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
        </select>

        {needSpouse && (
          <>
            <label style={lbl}>Nom du conjoint{req}</label>
            <input style={inp} value={f.spouseName} onChange={set('spouseName')} />
            {/* SD Worx demande aussi sa date de naissance et son revenu (Aurélie 30-09-2026) */}
            <label style={lbl}>Date de naissance du conjoint{req}</label>
            <input style={inp} type="date" value={f.spouseBirthDate} onChange={set('spouseBirthDate')} />

            <label style={lbl}>Le conjoint travaille ?{req}</label>
            <select style={inp} value={f.spouseWorks} onChange={set('spouseWorks')}>
              <option value="">— choisir —</option>
              <option value="true">Oui</option>
              <option value="false">Non</option>
            </select>

            {f.spouseWorks === 'true' && (
              <>
                <label style={lbl}>Revenu du conjoint{req}</label>
                <input style={inp} value={f.spouseIncome} onChange={set('spouseIncome')}
                  placeholder="ex. 2 100 € net / mois" maxLength={100} />
              </>
            )}
          </>
        )}

        <div style={{ marginTop: 18, padding: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10 }}>
          <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>Carte d'identité{hasCi ? '' : req}</div>
          {hasCi ? (
            <div style={{ fontSize: 13, color: '#166534', marginTop: 6 }}>
              ✅ Déjà au dossier. Ne rechargez les photos que si votre carte a changé.
            </div>
          ) : (
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 6 }}>
              Photo nette du <strong>recto</strong> et du <strong>verso</strong> (JPG/PNG/PDF, max 10 Mo).
            </div>
          )}
          <label style={lbl}>Recto</label>
          <input style={{ fontSize: 14 }} type="file" accept="image/jpeg,image/png,application/pdf"
            disabled={state === 'sending'} onChange={(e) => setRecto(e.target.files?.[0] || null)} />
          <label style={lbl}>Verso</label>
          <input style={{ fontSize: 14 }} type="file" accept="image/jpeg,image/png,application/pdf"
            disabled={state === 'sending'} onChange={(e) => setVerso(e.target.files?.[0] || null)} />
        </div>

        {error && (
          <div style={{ marginTop: 14, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 10, color: '#991b1b', fontSize: 13 }}>
            {error}
          </div>
        )}

        <button type="submit" disabled={state === 'sending'}
          style={{
            marginTop: 18, width: '100%', padding: '13px 0', fontSize: 16, fontWeight: 700,
            background: missing.length ? '#94a3b8' : '#2563eb', color: '#fff', border: 'none', borderRadius: 10,
            cursor: state === 'sending' ? 'wait' : 'pointer',
          }}>
          {state !== 'sending' ? 'Envoyer mon dossier' : preparation ? 'Préparation des photos…' : 'Envoi en cours…'}
        </button>
        {missing.length > 0 && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
            À compléter : {missing.join(' · ')}
          </div>
        )}
      </form>
    </div>
  );
}
